/**
 * The **affordance record** — one frozen object per activatable thing a combatant carries,
 * replacing the parallel string-keyed maps that used to describe the same fact five different
 * ways (`decisions/0176`).
 *
 * **The problem it dissolves.** A combatant's granted-action economy was frozen into three
 * parallel maps on `base` (`actionIds`, `actionUsageCaps`, `actionRecharge`) and ledgered into
 * two more on `current` (`discharged`, plus the `scene_use:`/`limited:` prefixes of
 * `resources`) — all five since deleted (`affordance-table` Phase 3.1). All five had to agree on one **hand-built string key** whose grammar was
 * *class-dependent* — `<itemId>:<index>` for the granted class, a bare pack id for the
 * pilot/frame-effect class — and every reader re-derived that key with a `lastIndexOf(":")`
 * that could only be correct if the reader already knew the class. Three maps that must agree
 * on one hand-built key is a struct that got flattened; this is the struct.
 *
 * A reader asks for the record **once** and gets the cap, the pool and the recharge target off
 * one object. There is no second lookup to key wrong.
 *
 * **Shipped.** `affordance-table` Phases 1-2 wrote the records alongside the old maps and moved
 * every reader onto them, and **Phase 3.1 deleted the five maps outright** (encounter schema
 * **v18**, no migration — a pre-v18 world fails to load loudly rather than coming up with an empty
 * economy). This record and {@link AffordanceStateEntry} are now the ONLY description of the frozen
 * and play-time action economy. The one door parser is `resolveAffordance`
 * (`rules/encounter/economy/carriedActions.ts`): it turns a wire id into a record once, after the
 * carrier class is known, and every downstream reader takes the record.
 */

/**
 * A frozen affordance's **rate**: a RAW `frequency` string (`1/turn`, `1/round`, `1/scene`) that
 * resets on its own grain. Keyed per affordance on its own ledger slot, never pooled: a frequency
 * is per-action by RAW construction.
 *
 * A Limited charge pool is a different fact and lives in {@link AffordanceLimited} beside it
 * (`decisions/0202`). One action can carry both: Bombing Bay's Drop Bomb is `1/round` and Limited 3.
 */
export type AffordanceCap = {
  grain: "turn" | "round" | "scene";
  max: number;
};

/**
 * A frozen affordance's **Limited charge pool**: `tg_limited {N}` on the item (or the action), or an
 * authored `limitedUses` facet. It lasts until a Full Repair, so the commit extractor persists its
 * spent count (`limitedSystemUsedCounts`), and its presence is the whole Limited marker.
 *
 * **`pool`** is the shared ledger slot. `decisions/0123`'s offset addressing lets one item mint more
 * than one affordance (a thrown grenade and a deployable mine off Smoke/HEX Charges), and both draw
 * the same RAW charge pool. Every item-level freezer sets it to the item id, so a pool slot never
 * collides with a scene-grain rate on the affordance's own `key`. Absent, the pool counts on `key`.
 */
export type AffordanceLimited = {
  max: number;
  pool?: string;
};

/**
 * **Where an affordance comes from** — the discriminated union that carries the carrier class
 * *structurally*, so no reader ever has to recover it by parsing a string.
 *
 * The two members are the two carrier classes, and **both id shapes are correct**; which one is
 * correct is class-determined:
 *
 * - **`granted`** — mech systems, pilot gear, NPC features. One entry mints one affordance per
 *   position in `decisions/0123`'s unified affordance space (`actions[]` first, then
 *   `deployables[]`, one flat index), so `index` is a *compendium position*, not an
 *   `actions[]` offset. A bare item id names the *entry*; the thing that gets capped,
 *   discharged and ledgered is the *affordance*, which is why the granted class needs the
 *   index at all.
 * - **`effect`** — frame traits, frame core systems, core bonuses, talent ranks, NPC
 *   Trait/Reaction/Tech feature ids. **No index, deliberately.** For this class the bare id is
 *   not an under-specified anything: it is the whole and correct address, it has no composite
 *   equivalent, and its cap is frozen *entry-level* by construction. One bare activation of
 *   `everest_hyperspec_fuel_injector` fires **both** of Power Up's authored clauses and
 *   consumes the one 1/scene cap frozen for the entry as a whole — so an entry-level fire is
 *   the only thing that could consume it correctly. A talent rank's `<talentId>:<rank>` id is
 *   an `effect` `entityId` too: it is lexically shaped like a granted composite and means
 *   something structurally different, and this union is where that stops being a hazard.
 */
export type AffordanceSource =
  | { kind: "granted"; itemId: string; index: number }
  | { kind: "effect"; entityId: string };

/**
 * **The one function that spells an affordance key.** Every writer of {@link Affordance.key}
 * goes through here, so there is exactly one place the serialization lives.
 *
 * It reproduces today's strings **byte-for-byte** — `<itemId>:<index>` for the granted class,
 * the bare `entityId` for the effect class — which is what makes Phase 1 an expansion rather
 * than a migration: the wire, the frozen maps and the state ledgers all keep their existing
 * spellings while the record is added beside them.
 *
 * **Never construct `${id}:0` anywhere else.** `:0` happens to be right for every bare-fired
 * entry in today's corpus and is wrong the first time a deployable-bearing entry is fired bare
 * (`ms_pattern_a_smoke_charges` mints `:0` for its grenade and `:1` for its mine). A caller that
 * has a record has the index already; a caller that doesn't must resolve against the carrier's
 * actual records rather than guessing one.
 */
export function affordanceKey(source: AffordanceSource): string {
  return source.kind === "granted" ? `${source.itemId}:${source.index}` : source.entityId;
}

/**
 * One **frozen affordance** — everything the economy needs to know about one activatable thing,
 * on one object.
 *
 * `key` is the serialized id ({@link affordanceKey}), which is also the ledger key on
 * `CombatantCurrent.affordanceState`; `source` is the structured, class-aware address; `cap`,
 * `limited` and `recharge` are the three frozen economy facts, each absent when the affordance
 * carries none (unrestricted — the common case).
 *
 * The three are separate on purpose and must not be conflated: `cap` is a rate that resets on its
 * grain, `limited` is a charge pool that lasts until a Full Repair (`decisions/0202`), and Recharge
 * (RAW Core p.281) is a stochastic "fired, awaiting a d6 ≥ N" gate. Every gate checks every fact
 * present, and each fact spends its own ledger slot.
 */
export type Affordance = {
  /** The serialized id — the ONE spelling, written by {@link affordanceKey}. */
  key: string;
  /** The structured, class-aware address this key serializes. */
  source: AffordanceSource;
  /** The RAW frequency (rate), when the affordance carries one. */
  cap?: AffordanceCap;
  /** The `Limited {N}` charge pool, when the affordance carries one. */
  limited?: AffordanceLimited;
  /** The `tg_recharge` target number N, when the affordance carries one (NPC features today). */
  recharge?: number;
  /**
   * The carrying item's display name (`"Frag Grenades"`, `"HEX Charges"`, `"Impale"`), frozen at
   * spawn off the compendium record the mint already reads (`narration-ledger-retirement` 2.2).
   * The engine holds no compendium, so this is where the referee reads `about.item`'s name for an
   * activation. Granted class only; absent on the effect class and on hand-built records.
   */
  name?: string;
};

/**
 * One affordance's (or weapon instance's) **play-time state** — the single ledger entry that
 * replaced `current.discharged` plus the `scene_use:`/`limited:` prefixes of `current.resources`.
 *
 * `uses` is the consumed count against {@link Affordance.cap} (a scene-grain rate, at `key`) or
 * {@link Affordance.limited} (at `pool ?? key`); `discharged` is Recharge's binary
 * "fired, awaiting the roll" flag. Both optional: an untouched affordance has no entry at all.
 *
 * **Weapons share this ledger** (`decisions/0176` ruling 1). A weapon's recharge target already
 * lives on its own `WeaponProfile`; only its *state* was string-keyed, so weapon instances key
 * in here by `weaponInstanceKey` and "used or discharged" has one shape regardless of what the
 * thing is. The frozen side for weapons is untouched. (Phase 2 of `affordance-table`.)
 */
export type AffordanceStateEntry = {
  /** Consumed uses against a scene-grain `cap` or a `limited` pool keyed on this slot. */
  uses?: number;
  /** Present ⇒ fired and awaiting a Recharge roll ≥ the frozen `recharge` target. */
  discharged?: true;
};

/**
 * **The one exact-key lookup into a combatant's frozen affordance table.** Returns the record
 * whose {@link Affordance.key} is `key`, or `undefined` when the carrier froze none.
 *
 * This is the record-shaped replacement for the deleted `base.actionUsageCaps[id]` /
 * `base.actionRecharge[id]` raw map reads (`affordance-table` Phase 3.1), and it reproduces them
 * bit-for-bit: an EXACT match, never a class-aware resolution and never a bare→composite
 * normalization. A caller holding an unresolved WIRE id must go through `resolveAffordance`
 * (`rules/encounter/economy/carriedActions.ts`) instead — that is the one door, and it is where
 * the class is settled before any colon is parsed. Use this only where the old code indexed a
 * frozen map with a key it already knew was the ledger key: a core-verb id (`skirmish`), a
 * `actionIdForIntent` output, an affordance key already in hand.
 *
 * **It sees only the FROZEN table.** Some composites exist only as the enumerate-time overlay
 * mint and are never frozen; `carriedAffordances` is the derivation that unions those in. This
 * lookup deliberately does not, exactly as the old raw map read did not.
 */
export function findAffordance(
  affordances: readonly Affordance[] | undefined,
  key: string,
): Affordance | undefined {
  return affordances?.find((a) => a.key === key);
}

/**
 * Build one **granted-class** {@link Affordance} — a mech system's, pilot gear's or NPC feature's
 * action at position `index` in `decisions/0123`'s unified affordance space.
 *
 * **Use this instead of writing a record literal, and never hand-spell the `key`.** A fixture that
 * types `{ key: "pg_patch:0", source: { kind: "granted", itemId: "pg_patch", index: 0 } }` has
 * restated the serialization by hand in a second place — which is exactly the string-keying
 * `decisions/0176` exists to delete, reappearing in the test corpus. Here the key is
 * {@link affordanceKey}'s output by construction, so a fixture cannot disagree with the engine
 * about how an id is spelled.
 *
 * The spawn adapters go through `buildAffordances` (`rules/encounter/spawn/actionFreeze.ts`),
 * which joins these sources against the caps its sibling freezers produce; this constructor is
 * for the hand-built case — a test fixture, an `edit_combatant_base` payload — where the cap and
 * the recharge target are known up front rather than looked up.
 */
export function grantedAffordance(
  itemId: string,
  index: number,
  extra: { cap?: AffordanceCap; limited?: AffordanceLimited; recharge?: number; name?: string } = {},
): Affordance {
  const source = { kind: "granted", itemId, index } as const;
  return {
    key: affordanceKey(source),
    source,
    ...(extra.cap !== undefined ? { cap: extra.cap } : {}),
    ...(extra.limited !== undefined ? { limited: extra.limited } : {}),
    ...(extra.recharge !== undefined ? { recharge: extra.recharge } : {}),
    ...(extra.name !== undefined ? { name: extra.name } : {}),
  };
}

/**
 * Build one **effect-class** {@link Affordance} — a frame trait, frame core system, core bonus,
 * talent rank or NPC Trait/Reaction/Tech feature id. **No index, deliberately**: for this class
 * the bare id is the whole and correct address and its cap is frozen entry-level, so one bare
 * activation fires every authored clause and consumes the one cap frozen for the entry.
 *
 * The {@link grantedAffordance} sibling; same rule about never hand-spelling the key.
 */
export function effectAffordance(
  entityId: string,
  extra: { cap?: AffordanceCap; limited?: AffordanceLimited; recharge?: number } = {},
): Affordance {
  const source = { kind: "effect", entityId } as const;
  return {
    key: affordanceKey(source),
    source,
    ...(extra.cap !== undefined ? { cap: extra.cap } : {}),
    ...(extra.limited !== undefined ? { limited: extra.limited } : {}),
    ...(extra.recharge !== undefined ? { recharge: extra.recharge } : {}),
  };
}

/**
 * The **granted-class** affordance keys a frozen table carries, in table order — the record-shaped
 * successor to the deleted `base.actionIds` (`affordance-table` Phase 3.1).
 *
 * Effect-class records (frame traits, frame core systems, core bonuses) are filtered out, exactly
 * as `actionIds` never held them, so a read written against the old list keeps its exact meaning.
 *
 * **This is the FROZEN table only.** The live "what does this combatant carry right now" question
 * is `carriedAffordances(combatant, overlay)` (`rules/encounter/economy/carriedActions.ts`) — the
 * frozen table unioned with the enumerate-time overlay mint, minus a destroyed system's. Reach for
 * that at any gate or bar; reach for this only where the old code read the frozen list directly.
 */
export function grantedAffordanceKeys(affordances: readonly Affordance[] | undefined): string[] {
  return (affordances ?? []).filter((a) => a.source.kind === "granted").map((a) => a.key);
}
